import { useQuery } from '@tanstack/react-query';
import { features } from '@api/features';
import type { FeaturesResponse } from '@api/features';

export const featuresQueryKey = ['features'] as const;

export type FeatureFlag = keyof FeaturesResponse['features'];

// Short cache: flags change rarely, but a stale "enabled" would make the UI keep
// blocking operations the backend already accepts. 60s bounds that window while
// still deduping the fetch across a burst of mounts/focus events (vs refetching
// on every one). An absent/loading flag reads as off, matching
// FeatureFlipperService's opt-in semantics, so the UI never enforces a rule the
// backend isn't.
const FEATURES_STALE_TIME_MS = 60_000;

/** Reads the backend feature flags the UI needs to mirror server behaviour. */
export function useFeatures() {
  const { data } = useQuery({
    queryKey: featuresQueryKey,
    queryFn: async () => (await features.get()).data,
    staleTime: FEATURES_STALE_TIME_MS,
  });

  return {
    features: data?.features,
    imageValidationEnabled: data?.features?.validates_product_images ?? false,
  };
}

export function useFeatureFlag(flag: FeatureFlag): boolean {
  const { features } = useFeatures();
  return features?.[flag] ?? false;
}
